import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Card,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Autocomplete,
  Button,
} from "@mui/material";
import {
  MyLocation as MyLocationIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useAppSelector, useAppDispatch } from "@hooks/redux";
import {
  updateApiary,
  deleteApiary,
  fetchApiariesAndGroups,
  setSelectedApiary,
  setApiaryEdited,
} from "@store/slices/locationsSlice";
import NominatimAutocomplete from "@components/common/NominatimAutocomplete";
import ColorPicker from "@components/common/ColorPicker";
import SaveToolbar from "@components/common/SaveToolbar";
import UnsavedChangesDialog from "@components/common/UnsavedChangesDialog";
import { useUnsavedChanges } from "@hooks/useUnsavedChanges";
import { KENYA_LOCATIONS, County, Constituency } from "@constants/kenyaLocations";

interface ApiaryFormData {
  name: string;
  address: string;
  country_code: string;
  city: string;
  postal_code: string;
  street: string;
  street_no: string;
  latitude: number | undefined;
  longitude: number | undefined;
  hex_color: string;
  roofed: boolean;
}

const ApiaryEdit: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const { selectedApiary, loadingState, error, apiaries } = useAppSelector(
    (state) => state.locations,
  );

  const [showLoadingIcon, setShowLoadingIcon] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Location State
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  const [selectedConstituency, setSelectedConstituency] = useState<Constituency | null>(null);
  const [selectedWard, setSelectedWard] = useState<string | null>(null);
  const [village, setVillage] = useState('');
  const [landmark, setLandmark] = useState('');

  const apiaryId = parseInt(id || "0", 10);
  const apiary =
    apiaries.find((item) => item.id === apiaryId) || selectedApiary;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<ApiaryFormData>({
    defaultValues: {
      name: "",
      address: "",
      country_code: "KE",
      city: "",
      postal_code: "",
      street: "",
      street_no: "",
      latitude: undefined,
      longitude: undefined,
      hex_color: "#F8B133",
      roofed: false,
    },
  });

  // Track form changes
  useEffect(() => {
    if (isDirty) {
      dispatch(setApiaryEdited(true));
    }
  }, [isDirty, dispatch]);

  // Navigation guard
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    {
      when: isDirty && !showLoadingIcon,
      message: "You have unsaved changes. Are you sure you want to leave?",
      onConfirm: () => {
        dispatch(setApiaryEdited(false));
      },
    },
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchApiariesAndGroups());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (apiary) {
      setValue("name", apiary.name);
      setValue("address", apiary.address || "");
      setValue("country_code", apiary.country_code || "KE");
      setValue("city", apiary.city || "");
      setValue("postal_code", apiary.postal_code || "");
      setValue("street", apiary.street || "");
      setValue("street_no", apiary.street_no || "");
      setValue("latitude", apiary.latitude);
      setValue("longitude", apiary.longitude);
      setValue("hex_color", apiary.hex_color || "#F8B133");
      setValue("roofed", apiary.roofed || false);
      dispatch(setSelectedApiary(apiary));

      // Populate location state
      const county = KENYA_LOCATIONS.find(c => c.name === apiary.city);
      if (county) {
        setSelectedCounty(county);

        if (apiary.street) {
          const parts = apiary.street.split(', ');
          // Try to match constituency (last part)
          const constituencyName = parts[parts.length - 1];
          const constituency = county.constituencies.find(c => c.name === constituencyName);

          if (constituency) {
            setSelectedConstituency(constituency);

            // Try to match ward (second to last part)
            if (parts.length >= 2) {
              const wardName = parts[parts.length - 2];
              if (constituency.wards.includes(wardName)) {
                setSelectedWard(wardName);

                // Village is the rest
                if (parts.length >= 3) {
                  setVillage(parts.slice(0, parts.length - 2).join(', '));
                }
              }
            }
          } else {
            // Fallback: just put everything in village if we can't parse
            setVillage(apiary.street);
          }
        }
      }
      setLandmark(apiary.street_no || '');
    }
  }, [apiary, setValue, dispatch]);

  // Update form values when location parts change
  useEffect(() => {
    if (selectedCounty) {
      setValue('city', selectedCounty.name, { shouldDirty: true });
      setValue('country_code', 'KE', { shouldDirty: true });
    }
  }, [selectedCounty, setValue]);

  useEffect(() => {
    const parts = [];
    if (village) parts.push(village);
    if (selectedWard) parts.push(selectedWard);
    if (selectedConstituency) parts.push(selectedConstituency.name);

    const newStreet = parts.join(', ');
    if (apiary && newStreet !== apiary.street) {
      setValue('street', newStreet, { shouldDirty: true });
    }
  }, [selectedConstituency, selectedWard, village, setValue, apiary]);

  useEffect(() => {
    if (apiary && landmark !== apiary.street_no) {
      setValue('street_no', landmark, { shouldDirty: true });
    }
  }, [landmark, setValue, apiary]);

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setValue('latitude', parseFloat(position.coords.latitude.toFixed(6)), { shouldDirty: true });
          setValue('longitude', parseFloat(position.coords.longitude.toFixed(6)), { shouldDirty: true });
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Could not get current location. Please ensure location services are enabled.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const getTitle = () => {
    return apiary ? `Edit Apiary: ${apiary.name}` : "Edit Apiary";
  };

  const isApiaryEditable = () => {
    return apiary ? apiary.owner || apiary.editable : false;
  };

  const handleLocationSelect = (location: {
    address: string;
    lat: number;
    lng: number;
    city?: string;
    country_code?: string;
    postal_code?: string;
    street?: string;
    street_number?: string;
  }) => {
    setValue("address", location.address, { shouldDirty: true });
    setValue("latitude", location.lat, { shouldDirty: true });
    setValue("longitude", location.lng, { shouldDirty: true });
    if (location.city) {
      const county = KENYA_LOCATIONS.find(c => c.name === location.city || c.name.includes(location.city || ''));
      if (county) setSelectedCounty(county);
      else setValue("city", location.city, { shouldDirty: true });
    }
    if (location.country_code)
      setValue("country_code", location.country_code, { shouldDirty: true });
    if (location.postal_code)
      setValue("postal_code", location.postal_code, { shouldDirty: true });
  };

  const handleColorChange = (color: string) => {
    setValue("hex_color", color, { shouldDirty: true });
  };

  const handleSaveApiary = async (data: ApiaryFormData) => {
    if (!apiary || !isApiaryEditable()) return;

    try {
      setShowLoadingIcon(true);
      setErrorMessage("");

      const apiaryData = {
        name: data.name,
        address: data.address,
        country_code: data.country_code,
        city: data.city,
        postal_code: data.postal_code,
        street: data.street,
        street_no: data.street_no,
        latitude: data.latitude || undefined,
        longitude: data.longitude || undefined,
        hex_color: data.hex_color,
        roofed: data.roofed,
      };

      await dispatch(updateApiary({ id: apiary.id, data: apiaryData }));
      dispatch(setApiaryEdited(false));
      navigate("/");
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to update apiary");
    } finally {
      setShowLoadingIcon(false);
    }
  };

  const handleDeleteApiary = async () => {
    if (!apiary || !apiary.owner) return;

    if (
      window.confirm(
        `Are you sure you want to delete apiary "${apiary.name}"? This action cannot be undone.`,
      )
    ) {
      try {
        setShowLoadingIcon(true);
        await dispatch(deleteApiary(apiary.id));
        dispatch(setApiaryEdited(false));
        navigate("/");
      } catch (err: any) {
        setErrorMessage(err.message || "Failed to delete apiary");
      } finally {
        setShowLoadingIcon(false);
      }
    }
  };

  if (loadingState === "pending" || (loadingState === "idle" && !apiary)) {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    );
  }

  if (!apiary) {
    return (
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
        <Alert severity="error">Apiary not found.</Alert>
      </Container>
    );
  }

  if (!isApiaryEditable()) {
    return (
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
        <Alert severity="warning">
          You do not have permission to edit this apiary.
        </Alert>
      </Container>
    );
  }

  const formValues = watch();
  const isValid = !errors.name && !!formValues.name;

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
      {/* Save Toolbar */}
      <SaveToolbar
        onSave={handleSubmit(handleSaveApiary)}
        onDelete={apiary?.owner ? handleDeleteApiary : undefined}
        saveDisabled={!isValid || showLoadingIcon}
        loading={showLoadingIcon}
        showDelete={apiary?.owner}
      />

      <Typography
        variant={isMobile ? "h5" : "h4"}
        component="h1"
        gutterBottom
        sx={{
          textAlign: isMobile ? "center" : "left",
          mb: isMobile ? 2 : 3,
          mt: 2,
        }}
      >
        {getTitle()}
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(handleSaveApiary)}>
            {/* Location Settings */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="overline"
                display="block"
                gutterBottom
                color="text.secondary"
              >
                Location settings
              </Typography>

              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: "background.paper",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Apiary name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Name*"
                          fullWidth
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          inputProps={{ maxLength: 30 }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={8}>
                    <Typography variant="subtitle2" gutterBottom>
                      Apiary Color
                    </Typography>
                    <ColorPicker
                      color={formValues.hex_color}
                      onChange={handleColorChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mt: 2 }}>
                      <Controller
                        name="roofed"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} />}
                            label="Roofed"
                          />
                        )}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Is this apiary covered?
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Place Details */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="overline"
                display="block"
                gutterBottom
                color="text.secondary"
              >
                Place details
              </Typography>

              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 3,
                  backgroundColor: "background.paper",
                }}
              >
                <Grid container spacing={isMobile ? 2 : 3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Search Address
                    </Typography>
                    <NominatimAutocomplete
                      onLocationSelect={handleLocationSelect}
                      placeholder="Start typing to search for location..."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Or enter details manually:
                    </Typography>
                  </Grid>

                  {/* County */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={KENYA_LOCATIONS}
                      getOptionLabel={(option) => option.name}
                      value={selectedCounty}
                      onChange={(_, newValue) => {
                        setSelectedCounty(newValue);
                        setSelectedConstituency(null);
                        setSelectedWard(null);
                      }}
                      renderInput={(params) => <TextField {...params} label="County" placeholder="Select County" />}
                    />
                  </Grid>

                  {/* Constituency */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={selectedCounty?.constituencies || []}
                      getOptionLabel={(option) => option.name}
                      value={selectedConstituency}
                      disabled={!selectedCounty}
                      onChange={(_, newValue) => {
                        setSelectedConstituency(newValue);
                        setSelectedWard(null);
                      }}
                      renderInput={(params) => <TextField {...params} label="Constituency" placeholder="Select Constituency" />}
                    />
                  </Grid>

                  {/* Ward */}
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={selectedConstituency?.wards || []}
                      value={selectedWard}
                      disabled={!selectedConstituency}
                      onChange={(_, newValue) => setSelectedWard(newValue)}
                      renderInput={(params) => <TextField {...params} label="Ward" placeholder="Select Ward" />}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                      Specific Details
                    </Typography>
                  </Grid>

                  {/* Village / Estate */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Village / Estate"
                      fullWidth
                      value={village}
                      onChange={(e) => setVillage(e.target.value)}
                      placeholder="e.g. Kwa Njenga"
                    />
                  </Grid>

                  {/* Landmark */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nearest Landmark"
                      fullWidth
                      value={landmark}
                      onChange={(e) => setLandmark(e.target.value)}
                      placeholder="e.g. Near Primary School"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                      GPS Coordinates
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="latitude"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          label="Latitude"
                          type="number"
                          fullWidth
                          inputProps={{ step: "any" }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="longitude"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          label="Longitude"
                          type="number"
                          fullWidth
                          inputProps={{ step: "any" }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </form>
        </Box>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </Container>
  );
};

export default ApiaryEdit;
